/** @jsx jsx */
import React, {
	type KeyboardEvent,
	useEffect,
	useCallback,
	useRef,
	cloneElement,
	isValidElement,
	type FocusEvent,
	type MouseEvent,
	memo,
	useState,
} from 'react';
import { css, jsx } from '@compiled/react';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project';
import { CELL_WRAPPER_TEST_ID } from '@atlassian/jira-business-fields/cell-wrapper';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { fg } from '@atlassian/jira-feature-gating';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { DOWN, LEFT, RIGHT } from '../../../common/constants';
import type { CellNavigationProps, CellOverrides } from '../../../common/types/cell-navigation';
import { CellContainer } from '../../../common/ui/list-cell';
import { useCellActions, useCellSelected } from '../../../controllers/cell-navigation';
import type { ColumnIndex, Direction, RowIndex } from '../../../controllers/cell-navigation/types';
import { ClipboardController } from '../../../controllers/clipboard';
import type { ListViewControllerData } from '../../../controllers/list-view-issues/types';
import { useTableRefStore } from '../../../controllers/table-ref';
import {
	KEY_LEFT,
	KEY_RIGHT,
	KEY_UP,
	KEY_DOWN,
	KEY_SPACE,
	KEY_ENTER,
	KEY_ESCAPE,
	KEY_TAB,
	VALID_HORIZONTAL_NAV_KEYS,
	VALID_NAVIGATION_KEYS,
	NAV_KEYS_TO_DIRECTION,
	getNewNavSupportedFieldTypes,
} from './constants';

const isNonCharacterKey = (event: KeyboardEvent) =>
	event.key.length > 1 || event.ctrlKey || event.metaKey;

const copiedFromStyles = css({
	'&::before': {
		display: 'block',
		position: 'absolute',
		content: '',
		width: 'calc(100% - 4px)',
		height: 'calc(100% - 4px)',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		border: `2px ${token('color.border.focused', colors.B200)} dashed`,
		borderRadius: token('border.radius', '4px'),
		backgroundColor: 'transparent',
		pointerEvents: 'none',
		zIndex: 0,
	},
	position: 'relative',
});

export const CellNavigation = memo(
	({
		children,
		columnIndex,
		rowIndex,
		field,
		onSendNodeSelected,
		onSendClearNodeSelection,
		issue,
		fieldId,
	}: {
		children: React.ReactNode;
		columnIndex: ColumnIndex;
		rowIndex: RowIndex;
		field?: BusinessIssueField;
		issue?: ListViewControllerData;
		fieldId?: string;
		onSendNodeSelected?: () => void;
		onSendClearNodeSelection?: () => void;
	}) => {
		const [
			{ isSelected = false, isCopiedFrom = false },
			{ setSelectedCell, clearSelectedCell, clearCopiedFromCell },
		] = useCellSelected({
			column: columnIndex,
			row: rowIndex,
			issueId: issue?.issueId,
			fieldId: field?.id,
		});

		const isSelectedRef = useRef<boolean>(isSelected);
		const prevIsSelected = usePreviousWithInitial(isSelected);
		const horizontalNavigationRef = useRef<boolean>(false);
		const wrapperRef = useRef<HTMLDivElement>(null);
		const cellRef = useRef<CellOverrides>(null);
		const { navigate } = useCellActions();
		const [{ tableRef }] = useTableRefStore();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const [editMode, setEditModeFn] = useState(false);
		const [inputValue, setInputValue] = useState<string | undefined>();
		const [isHovering, setIsHovering] = useState(false);
		const editModeRef = useRef<boolean>(editMode);
		editModeRef.current = editMode;

		const isEditable = field?.fieldConfig?.isEditable ?? true;

		const isNewKeyboardNavField =
			field?.type && getNewNavSupportedFieldTypes().has(field.type) && fg('jira_list_keyboard_nav');

		const setEditMode = useCallback(
			(nextEditMode: boolean) => {
				if (nextEditMode === editMode) {
					return;
				}

				if (!isNewKeyboardNavField || isEditable) {
					setEditModeFn(nextEditMode);
					// If edit mode is set to false, refocus on cell wrapper
					if (!nextEditMode && fg('list_view_cell_focus')) {
						setTimeout(() => {
							wrapperRef?.current?.focus();
						}, 0);
					}
				}
			},
			[editMode, isEditable, isNewKeyboardNavField],
		);

		useEffect(() => {
			// allow first render isSelected to call the fn
			if (isSelected === true && prevIsSelected === false) {
				onSendNodeSelected && onSendNodeSelected();
				horizontalNavigationRef.current = false;
			}

			if (isSelected === false && prevIsSelected === true) {
				onSendClearNodeSelection && onSendClearNodeSelection(); // only clear if prev value is true
			}
		}, [isSelected, onSendClearNodeSelection, onSendNodeSelected, prevIsSelected]);

		useEffect(() => {
			isSelectedRef.current = isSelected;
			if (isSelected && wrapperRef.current) {
				wrapperRef.current.focus();
			}
		}, [isSelected]);

		useEffect(
			() => () => {
				if (isSelectedRef.current) {
					clearSelectedCell();
				}
			},
			[clearSelectedCell],
		);

		useEffect(() => {
			if (isNewKeyboardNavField) {
				if (isSelected === false && editMode) {
					setEditMode(false);
				}
			}
		}, [editMode, isSelected, isNewKeyboardNavField, setEditMode]);

		const handleFocus = useCallback(() => {
			setSelectedCell(columnIndex, rowIndex);
		}, [columnIndex, rowIndex, setSelectedCell]);

		const handleBlur = useCallback(
			(event: FocusEvent<HTMLElement>) => {
				if (
					(!isNewKeyboardNavField || event.relatedTarget) &&
					!event.currentTarget?.contains(event.relatedTarget)
				) {
					const stop = cellRef.current?.onBlur?.(event);
					if (stop) {
						return;
					}
					clearSelectedCell();
				}
			},
			[clearSelectedCell, isNewKeyboardNavField],
		);

		const handleKeyDown = useCallback(
			// eslint-disable-next-line complexity
			(event: KeyboardEvent<HTMLElement>) => {
				if (isNewKeyboardNavField) {
					const stop = cellRef.current?.onKeyDown?.(event);
					if (stop) {
						return;
					}
				}

				const horizontalNavKeyPressed = VALID_HORIZONTAL_NAV_KEYS.has(event.key);
				const isTabPressed = event.key === KEY_TAB;
				const isValidKey = VALID_NAVIGATION_KEYS.has(event.key);

				if (event.shiftKey && !isTabPressed) {
					return;
				}

				if (!isValidKey) {
					if (isNewKeyboardNavField && !editMode && !isNonCharacterKey(event)) {
						setEditMode(true);
						fireUIAnalytics(
							createAnalyticsEvent({
								action: 'typeToEdit',
								actionSubject: 'listNavigated',
							}),
							{ fieldType: field.type },
						);

						setTimeout(() => {
							setInputValue(event.key);
						});
					}
					return;
				}

				const isScrolling = !!tableRef?.current?.table?.bodyRef?.state?.isScrolling;
				const navigationInProgress =
					isSelected && horizontalNavigationRef.current && horizontalNavKeyPressed;

				if (navigationInProgress || isScrolling) {
					return;
				}

				const fireNavigationAnalytics = () => {
					const EVENT_KEY = event.key === KEY_SPACE ? 'Space' : event.key;
					const attributes =
						isNewKeyboardNavField && ['Enter', 'Tab', 'Escape', 'Space'].includes(EVENT_KEY)
							? {
									isEditMode: editMode,
									fieldType: field.type,
								}
							: {};
					fireUIAnalytics(
						createAnalyticsEvent({
							action: EVENT_KEY,
							actionSubject: 'listNavigated',
						}),
						attributes,
					);
				};

				const handleNavigationEvent = (overrideDirection?: Direction) => {
					const direction = overrideDirection ?? NAV_KEYS_TO_DIRECTION[event.key];

					if (horizontalNavKeyPressed) {
						horizontalNavigationRef.current = true;
					}

					navigate(direction, tableRef);
				};

				if (isNewKeyboardNavField && editMode) {
					switch (event.key) {
						case KEY_ENTER: {
							setTimeout(() => {
								setEditMode(false);
								if (!fg('list_view_cell_focus')) {
									wrapperRef?.current?.focus();
								}
								handleNavigationEvent(DOWN);
								fireNavigationAnalytics();
							}, 1);
							return;
						}
						case KEY_ESCAPE: {
							setTimeout(() => {
								setEditMode(false);
								setInputValue(undefined);
								if (!fg('list_view_cell_focus')) {
									wrapperRef?.current?.focus();
								}
							});
							return;
						}
						case KEY_TAB: {
							setTimeout(() => {
								setEditMode(false);
								if (!fg('list_view_cell_focus')) {
									wrapperRef?.current?.focus();
								}
								handleNavigationEvent(event.shiftKey ? LEFT : RIGHT);
								fireNavigationAnalytics();
							}, 1);
							return;
						}
						case KEY_SPACE:
							// move selection down when pressing Space exits edit mode
							setTimeout(() => {
								if (!editModeRef.current) {
									if (!fg('list_view_cell_focus')) {
										wrapperRef?.current?.focus();
									}
									handleNavigationEvent(DOWN);
									fireNavigationAnalytics();
								}
							}, 1);
							return;
						case KEY_UP:
						case KEY_DOWN:
						case KEY_LEFT:
						case KEY_RIGHT:
							return;
						default:
					}
				}

				const tableDataLength = tableRef?.current?.table?.props?.data?.length || 0;
				const totalColumns = tableRef?.current?.table?.props?.columns?.length || 0;

				const isFirstTableItem = columnIndex === 0 && rowIndex === 0;
				const isLastTableItem =
					columnIndex === totalColumns - 1 && rowIndex === tableDataLength - 1;

				const isDirectionLeft = event.key === KEY_LEFT || (isTabPressed && event.shiftKey);
				const isDirectionRight = event.key === KEY_RIGHT || (isTabPressed && !event.shiftKey);

				if ((isFirstTableItem && isDirectionLeft) || (isLastTableItem && isDirectionRight)) {
					return;
				}

				event.preventDefault();
				fireNavigationAnalytics();

				switch (event.key) {
					case KEY_UP:
					case KEY_DOWN:
						handleNavigationEvent();
						break;

					case KEY_LEFT:
						if (columnIndex > 0) handleNavigationEvent();
						break;

					case KEY_RIGHT:
						if (columnIndex < totalColumns - 1) handleNavigationEvent();
						break;

					case KEY_TAB:
						handleNavigationEvent(event.shiftKey ? LEFT : RIGHT);
						break;

					case KEY_ENTER:
						if (!isNewKeyboardNavField || !editMode) {
							wrapperRef?.current?.click();
						}
						break;

					case KEY_SPACE:
						if (!isNewKeyboardNavField || !editMode) {
							wrapperRef?.current?.click();
						}
						break;

					case KEY_ESCAPE:
						if (!isNewKeyboardNavField || !editMode) {
							clearSelectedCell();
							clearCopiedFromCell();
							wrapperRef?.current?.blur();
						}
						break;

					default:
						break;
				}
			},
			[
				isNewKeyboardNavField,
				tableRef,
				isSelected,
				editMode,
				setEditMode,
				columnIndex,
				rowIndex,
				createAnalyticsEvent,
				navigate,
				clearSelectedCell,
				clearCopiedFromCell,
				field?.type,
			],
		);

		const handleClick = useCallback(
			(event: MouseEvent<HTMLElement>) => {
				if (isNewKeyboardNavField) {
					const stop = cellRef.current?.onClick?.(event);
					if (stop) {
						return;
					}
				}
				setEditMode(true);
			},
			[setEditMode, isNewKeyboardNavField],
		);

		const handleMouseEnter = useCallback(() => setIsHovering(true), [setIsHovering]);
		const handleMouseLeave = useCallback(() => setIsHovering(false), [setIsHovering]);

		const onInputChange = useCallback((value: string) => setInputValue(value), []);

		const getClone = () => {
			if (isValidElement<CellNavigationProps>(children)) {
				if (isNewKeyboardNavField) {
					return cloneElement(children, {
						isSelected,
						wrapperRef,
						cellRef,
						editMode,
						setEditMode,
						isHovering,
						inputValue,
						onInputChange,
					});
				}
				return cloneElement(children, {
					isSelected,
					wrapperRef,
					onFocus: handleFocus,
					onBlur: handleBlur,
					onKeyDown: handleKeyDown,
				});
			}

			return children;
		};

		if (isNewKeyboardNavField) {
			const getCellContainer = (
				props: { onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void } = {},
			) => (
				<CellContainer
					ref={wrapperRef}
					isFocused={isSelected}
					tabIndex={0}
					data-testid={CELL_WRAPPER_TEST_ID}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					onBlur={handleBlur}
					onFocus={handleFocus}
					onClick={handleClick}
					{...props}
				>
					<div css={isCopiedFrom && fg('jwm_list_new_copy_paste_behaviour') && [copiedFromStyles]}>
						{getClone()}
					</div>
					{isSelected && <ShortcutScope />}
				</CellContainer>
			);

			if (issue && field && fg('jwm_list_new_copy_paste_behaviour')) {
				return (
					<ClipboardController
						issue={issue}
						field={field}
						fieldId={fieldId}
						isSelected={isSelected}
						isEditMode={editMode}
						onKeyDown={handleKeyDown}
					>
						{getCellContainer()}
					</ClipboardController>
				);
			}

			return getCellContainer({
				onKeyDown: handleKeyDown,
			});
		}

		return <>{getClone()}</>;
	},
);
